/* React */
import React, { useRef, useState } from "react";

/* IonItem */
import {
  IonItem,
  IonIcon,
  InputInputEventDetail,
} from "@ionic/react";
import type { IonInputCustomEvent } from "@ionic/core";
import { chevronDown, chevronUp } from "ionicons/icons";

/* Utils */
import useClickOutsideHandler from "src/utils/hooks/useClickOutsideHandler";

/* Components */
import FilterLabel from "src/components/shared/labels/filter-label/FilterLabel";
import ItemsContainer from "src/components/insights/insights-filter/insights-filter-select/item-container/ItemContainer";
import Searchbar from "src/components/insights/custom-cohorts/cohort-builder/entity-selector/select/searchbar/Searchbar";

/* Stylesheet */
import styles from "./InsightsFilterSelect.module.scss";

/* Interfaces */
import {
  FilterProps,
  FilterType,
} from "src/shared/interfaces/insights-filter/InsightsFilter";

interface InsightsFilterSelectProps {
  itemKey: FilterType;
  label: string;
  item: FilterProps<string>;
  disabled?: boolean;
  handleItem: (itemKey: FilterType, value?: string) => void;
}

const InsightsFilterSelect: React.FC<
  InsightsFilterSelectProps
> = ({ itemKey, label, item, disabled, handleItem }) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [showItemContainer, setShowItemContainer] =
    useState<boolean>(false);

  const [showSearchbar, setShowSearchbar] =
    useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleItemsContainer = (value: boolean) => {
    setShowItemContainer(value);
  };

  useClickOutsideHandler({
    ref: dropdownRef,
    onClick: handleItemsContainer,
  });

  const handleSearchbar = (value: boolean) => {
    if (!value) {
      setSearchTerm("");
    }
    setShowSearchbar(value);
  };

  const handleSearchTerm = (
    e: IonInputCustomEvent<InputInputEventDetail>
  ) => {
    setSearchTerm(e.target.value as string);
  };

  // Remove already selected items
  const filterItems = (
    value: string,
    items: string[]
  ): string[] => {
    return items?.filter((item) => item !== value);
  };

  const validItems: string[] = Array.isArray(item.items)
    ? item.items
    : [];

  const length: number = filterItems(
    item.value! as string,
    validItems
  )?.length;

  const searchItems = validItems.filter((item) =>
    item.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filterDisabled =
    disabled || item.items?.length === 1;

  return (
    <div className={styles.insights_filter_select}>
      <FilterLabel label={label} />
      <div
        ref={dropdownRef}
        className={styles.wrapper_container}
      >
        <IonItem
          style={{
            "--padding-start":
              showSearchbar && showItemContainer
                ? "0px"
                : "16px",
            borderRadius: showItemContainer
              ? "8px 8px 0px 0px"
              : "42px",
          }}
          className={styles.item}
          lines="none"
          disabled={filterDisabled}
          button
          onClick={() => {
            handleItemsContainer(!showItemContainer);
            handleSearchbar(false);
          }}
        >
          {/* If filter is disabled, show a default string, otherwise item.value */}
          <div className={styles.value}>{item.value}</div>
          {length > 0 ? (
            <Searchbar
              scssProps={{
                color: "#abb8cd",
                background: "var(--ion-gradient-secondary)",
              }}
              showItems={showItemContainer}
              showSearchbar={showSearchbar}
              handleSearchbar={handleSearchbar}
              handleSearchTerm={handleSearchTerm}
            />
          ) : undefined}
          <IonIcon
            className={styles.icon}
            icon={
              showItemContainer ? chevronUp : chevronDown
            }
            slot="end"
          />
        </IonItem>
        {showItemContainer && !filterDisabled ? (
          <ItemsContainer
            itemKey={itemKey}
            items={filterItems(
              item.value!,
              searchItems || []
            )}
            disabled={disabled}
            handleItem={handleItem}
            handleItemContainer={handleItemsContainer}
          />
        ) : undefined}
      </div>
    </div>
  );
};

export default InsightsFilterSelect;
